import { Link } from "react-router-dom";
import { useCallback, useEffect, useRef, useState } from "react";
import { Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { deleteRecipe, getList } from "../../services/services";
import { ENDPOINT } from "../../services/api-endpoint";
import { useConfirmDialog } from "../../services/useConfirmDialog";
import TrurecipeGenerateRecipeModal from "./trurecipe-generate-recipe-modal";
import "./trurecipe-pages.scss";
import { formatRecipeCardTitle } from "./trurecipe-display";

type RecipeListRow = {
  id: number;
  name?: string;
  cardTitle?: string;
  subtitle?: string;
  materialCategoryName?: string | null;
  status?: string;
  paramCount?: number;
  criticalCount?: number;
  version?: string;
  createdate?: unknown;
  createdby?: string | null;
  jobUsageCount?: number;
  canDelete?: boolean;
};

function formatMesDate(d: unknown): string {
  if (d == null || d === "") return "—";
  const t = new Date(String(d)).getTime();
  if (!Number.isFinite(t)) return "—";
  try {
    return new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(t);
  } catch {
    return "—";
  }
}

const TrurecipeLibrary = () => {
  const [q, setQ] = useState("");
  const [items, setItems] = useState<RecipeListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const debounceRef = useRef<number>();
  const { confirm, ConfirmDialog } = useConfirmDialog();

  const load = useCallback(async (search: string) => {
    setLoading(true);
    try {
      const st = search.trim();
      const rows = await getList(ENDPOINT.RECIPE_LIST, {
        serachtext: st.length ? st : undefined,
      });
      setItems(Array.isArray(rows) ? (rows as RecipeListRow[]) : []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    window.clearTimeout(debounceRef.current);
    const delay = q.trim() ? 320 : 0;
    debounceRef.current = window.setTimeout(() => {
      void load(q);
    }, delay);
    return () => window.clearTimeout(debounceRef.current);
  }, [q, load]);

  const onDeleteRecipe = async (row: RecipeListRow) => {
    const title = formatRecipeCardTitle(
      (row.cardTitle && String(row.cardTitle).trim()) || row.name,
      `Recipe #${row.id}`,
    );
    const isConfirmed = await confirm({
      title: "Delete recipe?",
      message: `Remove "${title}"? This cannot be undone.`,
      confirmText: "Delete",
    });
    if (!isConfirmed) return;

    setDeletingId(row.id);
    try {
      await deleteRecipe(row.id);
      toast.success("Recipe deleted.");
      await load(q);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not delete recipe.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      <ConfirmDialog />
      <TrurecipeGenerateRecipeModal
        show={showGenerateModal}
        onHide={() => setShowGenerateModal(false)}
        onCreated={() => void load(q)}
      />
      <div className="tv-mes-v2-page">
        <div className="tv-mes-v2-shell">
          <header className="tv-mes-v2-hero tv-mes-v2-hero--split">
            <div>
              <p className="tv-mes-v2-page-desc">
                Manufacturing recipes auto-generated from technology + material + machine.
              </p>
            </div>
            <div className="tv-mes-v2-toolbar">
              <div className="tv-mes-v2-search">
                <i className="fa fa-search tv-mes-v2-search__icon" aria-hidden />
                <input
                  type="search"
                  className="tv-mes-v2-search__input"
                  placeholder="Search recipes..."
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  aria-label="Search recipes"
                />
              </div>
              <button
                type="button"
                className="tv-mes-v2-btn-orange"
                onClick={() => setShowGenerateModal(true)}
              >
                <i className="fa fa-plus" aria-hidden />
                Generate Recipe
              </button>
            </div>
          </header>

          {loading ? (
            <div className="tv-mes-v2-loading" role="status">
              <Spinner animation="border" size="sm" />
              <span>Loading recipes…</span>
            </div>
          ) : items.length === 0 ? (
            <div className="tv-mes-v2-panel">
              <h2 className="tv-mes-v2-panel__title">No recipes yet</h2>
              <p className="tv-mes-v2-page-desc" style={{ margin: 0 }}>
                Open a workflow template and use &quot;Create Recipe for This Workflow&quot; to add
                your first recipe. They will appear here automatically.
              </p>
              <Link to="/mes-workflow" className="tv-mes-v2-btn-orange tv-mes-v2-btn-orange--mt">
                Go to Workflow Templates
              </Link>
            </div>
          ) : (
            <div className="tv-mes-v2-recipe-grid">
              {items.map((r) => {
                const title = formatRecipeCardTitle(
                  (r.cardTitle && String(r.cardTitle).trim()) || r.name,
                  `Recipe #${r.id}`,
                );
                const sub =
                  (r.materialCategoryName && String(r.materialCategoryName).trim()) ||
                  (r.subtitle && String(r.subtitle).trim()) ||
                  "—";
                const rawStatus = (r.status ?? "draft").trim().toLowerCase();
                const st =
                  rawStatus === "approved"
                    ? "approved"
                    : rawStatus === "pending" ||
                        rawStatus === "in review" ||
                        rawStatus === "in_review" ||
                        rawStatus === "in-review"
                      ? "pending"
                      : "draft";
                const params = Number(r.paramCount ?? 0);
                const crit = Number(r.criticalCount ?? 0);
                const ver = r.version ?? "v1.0";
                const who = (r.createdby ?? "").trim() || "—";
                const when = formatMesDate(r.createdate);
                const canDelete =
                  r.canDelete ?? (st === "draft" || Number(r.jobUsageCount ?? 0) === 0);
                return (
                  <div key={r.id} className="tv-mes-v2-recipe-card">
                    <div className="tv-mes-v2-recipe-card__top">
                      <span className="tv-mes-v2-recipe-card__icon" aria-hidden>
                        <i className="fa fa-file-text-o" />
                      </span>
                      <div className="tv-mes-v2-recipe-card__top-actions">
                        <span className={`tv-mes-v2-status tv-mes-v2-status--${st}`}>
                          {st === "approved" ? "Active" : st === "pending" ? "Pending" : "Draft"}
                        </span>
                        {canDelete ? (
                          <button
                            type="button"
                            className="tv-mes-v2-recipe-card__delete"
                            aria-label={`Delete ${title}`}
                            title="Delete recipe"
                            disabled={deletingId === r.id}
                            onClick={() => void onDeleteRecipe(r)}
                          >
                            <i className="fa fa-trash-o" aria-hidden />
                          </button>
                        ) : null}
                      </div>
                    </div>
                    <Link to={`/mes-trurecipe/recipe/${r.id}`} className="tv-mes-v2-recipe-card__link">
                      <h2 className="tv-mes-v2-recipe-card__title">{title}</h2>
                      <p className="tv-mes-v2-recipe-card__sub">{sub}</p>
                      <p className="tv-mes-v2-recipe-card__meta">
                        {params} params · {crit} critical · {ver}
                      </p>
                      <p className="tv-mes-v2-recipe-card__foot">
                        Created by {who} — {when}
                      </p>
                    </Link>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default TrurecipeLibrary;
